/**
 * Estado mínimo para retomar a confirmação de OTP do owner após remount/reload.
 *
 * O handle é opaco e só vale no servidor; e-mail, código, sessão e qualquer
 * segredo permanecem exclusivamente em memória/cookie HttpOnly. A validação é
 * intencionalmente estrita: storage adulterado ou expirado não reidrata a UI.
 */
export const PENDING_OWNER_OTP_STORAGE_KEY = 'argink.owner-otp.pending.v1';
export const PENDING_OWNER_OTP_TTL_MS = 10 * 60_000;

export interface PendingOwnerOtp {
  verificationHandle: string;
  expiresAt: number;
}

const HANDLE_PATTERN = /^[A-Za-z0-9_-]{32,200}$/;

function getSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function isValidOwnerOtpHandle(value: unknown): value is string {
  return typeof value === 'string' && HANDLE_PATTERN.test(value);
}

export function clearPendingOwnerOtp(): void {
  try {
    getSessionStorage()?.removeItem(PENDING_OWNER_OTP_STORAGE_KEY);
  } catch {
    // Storage bloqueado (por exemplo, modo privado restritivo) não impede o
    // fluxo em memória; simplesmente não haverá retomada depois do remount.
  }
}

/** Lê somente um registro íntegro, ainda dentro da janela local de 10 minutos. */
export function readPendingOwnerOtp(now = Date.now()): PendingOwnerOtp | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(PENDING_OWNER_OTP_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed) ||
      Object.keys(parsed).length !== 2 ||
      !isValidOwnerOtpHandle((parsed as PendingOwnerOtp).verificationHandle) ||
      !Number.isSafeInteger((parsed as PendingOwnerOtp).expiresAt)
    ) {
      clearPendingOwnerOtp();
      return null;
    }

    const pending = parsed as PendingOwnerOtp;
    if (pending.expiresAt <= now || pending.expiresAt > now + PENDING_OWNER_OTP_TTL_MS) {
      clearPendingOwnerOtp();
      return null;
    }
    return pending;
  } catch {
    clearPendingOwnerOtp();
    return null;
  }
}

/** Persiste somente um handle já validado e sua expiração local. */
export function persistPendingOwnerOtp(verificationHandle: string, now = Date.now()): void {
  if (!isValidOwnerOtpHandle(verificationHandle)) return;

  try {
    getSessionStorage()?.setItem(
      PENDING_OWNER_OTP_STORAGE_KEY,
      JSON.stringify({ verificationHandle, expiresAt: now + PENDING_OWNER_OTP_TTL_MS }),
    );
  } catch {
    // O desafio em memória continua verificável; apenas a retomada não fica disponível.
  }
}
